import type {
	PurchaseOrderLineFromDetailsQuery,
	RetrospectiveLink,
	SettledState,
	LineSettlementState,
} from '../types';
import type { RetrospectiveLinkingStoreType } from './retrospectiveLinkingStore';
import { PARTIALLY_SETTLED_STATUS } from '@/features/PurchaseOrder/constants';

export const extractTargetLineId = (link: RetrospectiveLink): string | null => {
	const targetLine = link.targetLine;
	if (!targetLine || targetLine.__typename === 'HiddenByPermissions') {
		return null;
	}
	// targetLine is QuoteLineFromDetailsQuery | PurchaseOrderLineFromDetailsQuery
	return (targetLine as { id: string }).id;
};

export const getChangedTargetLineIds = (
	state: RetrospectiveLinkingStoreType
): Set<string> => {
	return new Set([
		...Object.values(state.links)
			.map(extractTargetLineId)
			.filter((id): id is string => id !== null),
		...Object.values(state.removedPersistedLinks)
			.map(extractTargetLineId)
			.filter((id): id is string => id !== null),
		...Object.keys(state.settledStates),
	]);
};

export const buildSettlementStates = (
	lines: PurchaseOrderLineFromDetailsQuery[],
	settledStates: Record<string, SettledState>
): LineSettlementState[] => {
	return lines.map((line) => {
		const changedState = settledStates[line.id];
		return {
			id: line.id,
			initiallySettled: line.isSettled,
			currentlySettled:
				changedState !== undefined
					? changedState.isSettled
					: line.isSettled,
		};
	});
};

export const requiresNotification = (
	states: LineSettlementState[],
	initialStatus: string | null | undefined
): boolean => {
	const allInitiallyUnsettled = states.every((s) => !s.initiallySettled);
	const allInitiallySettled = states.every((s) => s.initiallySettled);
	const someInitiallySettled = !allInitiallyUnsettled && !allInitiallySettled;

	const atLeastOneCurrentlySettled = states.some((s) => s.currentlySettled);
	const atLeastOneCurrentlyUnsettled = states.some(
		(s) => !s.currentlySettled
	);
	const allCurrentlySettled = states.every((s) => s.currentlySettled);
	const allCurrentlyUnsettled = states.every((s) => !s.currentlySettled);

	return (
		(allInitiallyUnsettled && atLeastOneCurrentlySettled) ||
		(allInitiallySettled && atLeastOneCurrentlyUnsettled) ||
		(someInitiallySettled && allCurrentlySettled) ||
		(initialStatus !== PARTIALLY_SETTLED_STATUS && allCurrentlyUnsettled)
	);
};
